import { ERROR_CODES } from '../errors/error-codes';
import { displayTimezoneFor } from '../domain/display-timezone';
/**
 * Görüşme talebi metin ve zaman biçimleme yardımcıları — web + mobil TEK kaynak.
 *
 * Blok 13'te web'de yazılmıştı; Blok 15'te mobil aynı eşlemeye ihtiyaç duyunca
 * buraya taşındı (CLAUDE.md: çoğaltılan iş mantığı `packages/shared`'a gider).
 * Joi TAŞIMAZ — React Native ve Edge güvenle içe aktarır.
 */
/**
 * Bağlama göre yeniden yazılan hata metinleri. Genel `errors.NOT_FOUND`
 * ("aradığın kayıt bulunamadı") onay penceresinde anlamsızdır: kullanıcı bir
 * TALEBİ yanıtlıyordur ve talep aradan kalkmıştır.
 */
const MEETING_ERROR_OVERRIDES = {
    [ERROR_CODES.NOT_FOUND]: 'meetings.errorGone',
    [ERROR_CODES.MEETING_REQUEST_NOT_FOUND]: 'meetings.errorGone',
};
const KNOWN_ERROR_CODES = new Set(Object.values(ERROR_CODES));
/**
 * Yazma eylemi (gönder/kabul/ret/iptal/engelle) hatasının METİN ANAHTARI.
 *
 * Diyalog istek başarılı olmadan KAPANMAZ (Blok 8 dersi); kapanmıyorsa hata da
 * yazılmak zorundadır — 409/429/403/404 hepsinin karşılığı vardır. Bilinmeyen
 * kod tek ortak yerde `INTERNAL_ERROR`a düşer; ekranda ham kod GÖSTERİLMEZ.
 */
export function meetingActionErrorKey(code, 
/**
 * `statusCode === 0` = istek sunucuya HİÇ ULAŞMADI (ağ yok / iptal). Kod
 * `INTERNAL_ERROR`a normalize edilir ve kullanıcı çevrimdışıyken "beklenmeyen
 * bir hata oluştu" okuyordu (QA Blok 15 P3). Sebep söylenebiliyorsa söylenir.
 */
statusCode) {
    if (!code)
        return null;
    if (statusCode === 0)
        return 'meetings.errorOffline';
    if (!KNOWN_ERROR_CODES.has(code))
        return `errors.${ERROR_CODES.INTERNAL_ERROR}`;
    return MEETING_ERROR_OVERRIDES[code] ?? `errors.${code}`;
}
/**
 * "Talep gönderildi" ekranının (10b) BAŞLIK anahtarı — web + mobil tek kaynak.
 *
 * Ekran açıkken talep yoklanır (13.10.2026); başlık sabit "{ad} yanıtı
 * bekleniyor" kalınca kabulden sonra gösterge ile çelişiyordu. Ret, başlıkta
 * ayrıca söylenmez ("Yanıtlandı, gerekçe paylaşılmaz" tonu); iptal ve süre
 * dolumu yanıt olmadığı için nötr "sonuçlandı" okur. `Record` tamdır: yeni bir
 * durum eklenirse derleme kırılır.
 */
const SENT_TITLE_KEYS = {
    pending: 'meetings.sentTitle',
    accepted: 'meetings.sentTitleAccepted',
    declined: 'meetings.sentTitleAnswered',
    cancelled: 'meetings.sentTitleClosed',
    expired: 'meetings.sentTitleClosed',
};
export function meetingSentTitleKey(status) {
    return SENT_TITLE_KEYS[status];
}
/**
 * Görüşme talebinin (ya da `eventFormat` + `eventTimezone` taşıyan herhangi bir
 * satırın) saatlerinin gösterileceği dilim (`displayTimezoneFor`).
 */
export function meetingRequestTimezone(request, viewerTimezone) {
    return displayTimezoneFor({
        format: request.eventFormat,
        eventTimezone: request.eventTimezone,
        viewerTimezone,
    });
}
/** "18 Ağu 2026 Sal" — gün başlığı, verilen dilimde (`displayTimezoneFor`). */
export function formatSlotDay(value, timezone, locale) {
    return formatter(locale, timezone, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(new Date(value));
}
/**
 * Aday saatleri gün başlığı altında toplar — web + mobil TEK kaynak.
 *
 * Gün sınırı GÖSTERİM diliminde çizilir (`displayTimezoneFor`: yüz yüzede
 * etkinlik, çevrimiçi/karmada cihaz dilimi); sıra `buildSlotCandidates`
 * çıktısının sırasıdır (yeniden sıralama YOK). Blok 15 tech-lead bulgusu:
 * kural iki uygulamada iki desende duruyordu (mobilde model, web'de bileşen
 * içinde `Map`) — CLAUDE.md gereği iş mantığı bileşende durmaz.
 */
export function groupSlotCandidates(candidates, timezone, locale) {
    const groups = [];
    for (const candidate of candidates) {
        const dayLabel = formatSlotDay(candidate.startsAt, timezone, locale);
        const last = groups[groups.length - 1];
        if (last && last.dayLabel === dayLabel) {
            last.items.push(candidate);
            continue;
        }
        groups.push({ dayLabel, items: [candidate] });
    }
    return groups;
}
/** "10:00 – 10:30" — aralık, verilen dilimde. */
export function formatSlotRange(startsAt, endsAt, timezone, locale) {
    const format = formatter(locale, timezone, { hour: '2-digit', minute: '2-digit' });
    return `${format.format(new Date(startsAt))} – ${format.format(new Date(endsAt))}`;
}
/** Gün + aralık tek satırda (kabul edilen saat, gönderildi katmanı). */
export function formatSlotFull(startsAt, endsAt, timezone, locale) {
    return `${formatSlotDay(startsAt, timezone, locale)} · ${formatSlotRange(startsAt, endsAt, timezone, locale)}`;
}
/**
 * Geçersiz IANA adı ekranı ÇÖKERTMEZ (translator ile aynı savunma hattı).
 * `Intl` geçersiz zaman diliminde `RangeError` fırlatır.
 */
function formatter(locale, timezone, options) {
    const tag = locale === 'en' ? 'en-US' : 'tr-TR';
    try {
        return new Intl.DateTimeFormat(tag, { ...options, timeZone: timezone });
    }
    catch {
        return new Intl.DateTimeFormat(tag, { ...options, timeZone: 'UTC' });
    }
}
/**
 * Görüşme alanları ÖZET SATIRI — web + mobil tek kaynak. Üç sayı bağımsızdır,
 * parçalar ayrı çekimlenir (Blok 23 çoğul dersi). "Yer bekleyen N görüşme"
 * (otomatik atama, 13.10.2026) sunucunun sayısıdır; 0 ya da bilinmiyorsa
 * (`null`) eklenmez, sayı uydurulmaz.
 */
export function meetingAreaSummaryText(t, counts) {
    const summary = t.t('meetingAreas.summary', {
        areasLabel: t.t('meetingAreas.areaCount', { count: counts.areas }),
        meetingsLabel: t.t('meetingAreas.acceptedMeetingCount', { count: counts.acceptedMeetings }),
        freeLabel: t.t('meetingAreas.freeSlotCount', { count: counts.freeSlots }),
    });
    if (!counts.unplacedMeetings)
        return summary;
    return t.t('meetingAreas.summaryWithUnplaced', {
        summary,
        unplacedLabel: t.t('meetingAreas.unplacedMeetingCount', { count: counts.unplacedMeetings }),
    });
}
//# sourceMappingURL=meeting-text.js.map